import { useState, useEffect, createContext, useContext, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import axios from "axios";
import Admin from "./Admin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_hi-project-31/artifacts/38hlrr0b_N%C3%A9vtelen%20terv%20%281%29.png";

// Contexts
const SiteSettingsContext = createContext(null);
const SiteContentContext = createContext(null);
const MenuContext = createContext(null);

const useSiteSettings = () => useContext(SiteSettingsContext);
const useSiteContent = () => useContext(SiteContentContext);
const useMenu = () => useContext(MenuContext);

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Helper to get full image URL
const getImageUrl = (src) => {
  if (!src) return "";
  if (src.startsWith("data:")) return src;
  if (src.startsWith("http")) return src;
  return `${BACKEND_URL}${src}`;
};

// Dynamic Icon Component
const DynamicIcon = ({ name, className }) => {
  // Base64 data URL (legacy inline)
  if (name && name.startsWith("data:")) {
    return (
      <img
        src={name}
        alt="icon"
        className={className}
        style={{ width: '2rem', height: '2rem', objectFit: 'contain' }}
      />
    );
  }

  // Uploaded icon served from MongoDB or legacy local path
  if (name && (name.startsWith("/api/uploads/") || name.startsWith("/api/site-images/"))) {
    return (
      <img 
        src={`${BACKEND_URL}${name}`} 
        alt="icon" 
        className={className}
        style={{ width: '2rem', height: '2rem', objectFit: 'contain' }}
      />
    );
  }
  
  // Get icon from Lucide
  const IconComponent = LucideIcons[name] || LucideIcons.Route;
  return <IconComponent className={className} />;
};

// Helper: resolve href in a location-aware way.
// - "/path" -> internal route (Link)
// - "#anchor" -> anchor on homepage. If we're not on "/" we go to "/#anchor".
const useResolvedHref = () => {
  const location = useLocation();
  return (href) => {
    if (!href) return { type: "external", href: "#" };
    if (href.startsWith("#")) {
      if (location.pathname !== "/") return { type: "anchor-cross", href: `/${href}` };
      return { type: "anchor-same", href };
    }
    if (href.startsWith("/")) return { type: "route", href };
    return { type: "external", href };
  };
};

const SmartLink = ({ href, children, className, onClick, ...rest }) => {
  const resolve = useResolvedHref();
  const r = resolve(href);
  if (r.type === "route") {
    return (
      <Link to={r.href} className={className} onClick={onClick} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={r.href} className={className} onClick={onClick} {...rest}>
      {children}
    </a>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuItems = useMenu();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0F172A]/95 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="PaintRoad Logo" className="w-10 h-10 object-contain" />
            <span className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase tracking-wider">
              PaintRoad
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {menuItems.map((item) => (
              <SmartLink
                key={item.id}
                href={item.href}
                data-testid={`nav-${item.id}`}
                className="text-white/80 hover:text-[#FFD700] transition-colors font-medium uppercase tracking-wide text-sm"
              >
                {item.label}
              </SmartLink>
            ))}
            <SmartLink
              href="#kapcsolat"
              data-testid="nav-cta"
              className="bg-[#FFD700] text-[#0F172A] px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-[#E6C200] transition-colors"
            >
              Kapcsolat
            </SmartLink>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden text-white"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <LucideIcons.X className="w-6 h-6" /> : <LucideIcons.Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden mt-4 pb-4"
          >
            {menuItems.map((item) => (
              <SmartLink
                key={item.id}
                href={item.href}
                onClick={() => setIsOpen(false)}
                className="block py-3 text-white/80 hover:text-[#FFD700] transition-colors font-medium uppercase tracking-wide"
              >
                {item.label}
              </SmartLink>
            ))}
            <SmartLink
              href="#kapcsolat"
              onClick={() => setIsOpen(false)}
              className="block mt-4 bg-[#FFD700] text-[#0F172A] px-6 py-3 font-bold uppercase tracking-wider text-center"
            >
              Kapcsolat
            </SmartLink>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section — pure CSS background art (no images), high performance
const Hero = () => {
  const content = useSiteContent();

  return (
    <section
      className="min-h-screen flex items-center pt-20 relative overflow-hidden bg-[#0F172A]"
      data-testid="hero-section"
    >
      {/* Layer 1: Diagonal road-stripe gradient */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, #FFD700 0 18px, transparent 18px 70px)"
        }}
      />

      {/* Layer 2: Radial gold glow top-right */}
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,215,0,0.18) 0%, rgba(255,215,0,0) 65%)"
        }}
      />

      {/* Layer 3: Radial glow bottom-left (cooler tone) */}
      <div
        className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(56,189,248,0.10) 0%, rgba(56,189,248,0) 65%)"
        }}
      />

      {/* Layer 4: SVG road-marker decoration (right side, decorative) */}
      <svg
        className="hidden md:block absolute right-8 top-1/2 -translate-y-1/2 w-[42%] max-w-[600px] opacity-[0.18] pointer-events-none"
        viewBox="0 0 600 600"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="roadGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#FFD700" stopOpacity="0.0" />
            <stop offset="0.4" stopColor="#FFD700" stopOpacity="1" />
            <stop offset="1" stopColor="#FFD700" stopOpacity="0.0" />
          </linearGradient>
        </defs>
        {/* Faux road centre lines */}
        {Array.from({ length: 8 }).map((_, i) => (
          <rect
            key={i}
            x={290 + i * 0.5}
            y={60 + i * 60}
            width="20"
            height="34"
            rx="2"
            fill="url(#roadGrad)"
          />
        ))}
        {/* Outer rail */}
        <rect x="80" y="20" width="6" height="560" fill="#FFD700" opacity="0.4" />
        <rect x="514" y="20" width="6" height="560" fill="#FFD700" opacity="0.4" />
      </svg>

      {/* Layer 5: Subtle dotted pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
          backgroundSize: "28px 28px"
        }}
      />

      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to right, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.55) 50%, rgba(15,23,42,0.85) 100%)"
        }}
      />

      <div className="max-w-7xl mx-auto px-6 py-20 w-full relative">
        <div className="max-w-2xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-4">
              <span className="inline-block bg-[#FFD700] text-[#0F172A] px-4 py-2 text-sm font-bold uppercase tracking-wider">
                {content?.hero_badge || "Professzionális Útfestés"}
              </span>
            </motion.div>
            <motion.h1
              variants={fadeInUp}
              className="font-['Barlow_Condensed'] text-6xl md:text-8xl font-extrabold text-white leading-none mb-6"
            >
              {content?.hero_title ? (
                content.hero_title.includes("ROAD") ? (
                  <>
                    {content.hero_title.split("ROAD")[0]}<span className="text-[#FFD700]">ROAD</span>{content.hero_title.split("ROAD")[1] || ""}
                  </>
                ) : content.hero_title
              ) : (
                <>PAINT<span className="text-[#FFD700]">ROAD</span></>
              )}
            </motion.h1>
            <motion.h2
              variants={fadeInUp}
              className="font-['Barlow_Condensed'] text-2xl md:text-3xl text-white/90 font-semibold uppercase tracking-wide mb-6"
            >
              {content?.hero_subtitle || "KFT."}
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-white/70 text-lg mb-8 max-w-md leading-relaxed"
            >
              {content?.hero_description || "Útburkolati jelek, parkolók, sportpályák és ipari létesítmények professzionális jelölése. Megbízhatóság és precizitás."}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <SmartLink
                href="#kapcsolat"
                data-testid="hero-cta-primary"
                className="btn-primary inline-flex items-center gap-2 shadow-[0_8px_30px_rgba(255,215,0,0.25)]"
              >
                {content?.hero_cta_primary || "Kapcsolatfelvétel"}
                <LucideIcons.ChevronDown className="w-5 h-5" />
              </SmartLink>
              <SmartLink
                href="#szolgaltatasok"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white border-2 border-[#FFD700] hover:bg-[#FFD700] hover:text-[#0F172A] font-bold uppercase tracking-[0.1em] px-8 py-4 transition-all duration-200 shadow-[0_4px_20px_rgba(0,0,0,0.4)]"
              >
                <LucideIcons.LayoutGrid className="w-5 h-5" />
                {content?.hero_cta_secondary || "Szolgáltatások"}
              </SmartLink>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const content = useSiteContent();

  const services = [
    {
      icon: content?.service_1_icon || "Route",
      title: content?.service_1_title || "Útburkolati Jelek Festése",
      description: content?.service_1_description || "Közutak, telephelyek, professzionális festése minőségi anyagokkal."
    },
    {
      icon: content?.service_2_icon || "Car",
      title: content?.service_2_title || "Parkoló Festés",
      description: content?.service_2_description || "Parkolóházak, mélygarázsok, udvarok, bevásárlóközpontok parkolóinak jelölése."
    },
    {
      icon: content?.service_3_icon || "Factory",
      title: content?.service_3_title || "Ipari Padlójelölés",
      description: content?.service_3_description || "Raktárak, üzemek, gyártósorok biztonsági sávjai és navigációs jelölései."
    }
  ];

  return (
    <section id="szolgaltatasok" className="py-24 bg-[#F3F4F6]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block bg-[#0F172A] text-[#FFD700] px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4"
          >
            {content?.services_badge || "Amit kínálunk"}
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-bold text-[#0F172A]"
          >
            {content?.services_title || "SZOLGÁLTATÁSAINK"}
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              data-testid={`service-card-${index}`}
              className="service-card bg-white border-2 border-gray-200 p-8 group cursor-pointer"
            >
              <div className="w-16 h-16 bg-[#0F172A] flex items-center justify-center mb-6 group-hover:bg-[#FFD700] transition-colors">
                <DynamicIcon 
                  name={service.icon} 
                  className="w-8 h-8 text-[#FFD700] group-hover:text-[#0F172A] transition-colors" 
                />
              </div>
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0F172A] mb-4">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Gallery Preview — homepage teaser only. Shows up to 6 thumbs + CTA to /referenciak.
const GalleryPreview = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const content = useSiteContent();

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/gallery`)
      .then((res) => {
        if (!mounted) return;
        setGalleryImages((res.data.images || []).slice(0, 6));
      })
      .catch(() => mounted && setGalleryImages([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section id="referenciak-preview" className="py-24 bg-white" data-testid="gallery-preview">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block bg-[#FFD700] text-[#0F172A] px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4"
          >
            {content?.gallery_badge || "Munkáink"}
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-bold text-[#0F172A]"
          >
            {content?.gallery_title || "REFERENCIÁK"}
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="mt-4 text-gray-600 max-w-2xl mx-auto"
          >
            {content?.gallery_description || "Egy ízelítő legutóbbi projektjeinkből — a teljes galériát a referenciák oldalon találja."}
          </motion.p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-10 h-10 border-4 border-[#FFD700] border-t-transparent rounded-full" />
          </div>
        ) : galleryImages.length === 0 ? (
          <div className="text-center py-8">
            <LucideIcons.ImageOff className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">Hamarosan feltöltjük a referencia képeinket.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
            {galleryImages.map((image, index) => (
              <Link
                key={image.id || index}
                to="/referenciak"
                data-testid={`preview-item-${index}`}
                className="relative aspect-square overflow-hidden group block"
              >
                <img
                  src={getImageUrl(image.thumbnail || image.src)}
                  alt={image.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-transparent" />
                <span className="absolute bottom-3 left-3 right-3 text-white text-xs font-semibold uppercase tracking-wider line-clamp-2">
                  {image.alt}
                </span>
              </Link>
            ))}
          </div>
        )}

        <div className="text-center">
          <Link
            to="/referenciak"
            data-testid="gallery-preview-cta"
            className="inline-flex items-center gap-2 bg-[#0F172A] text-[#FFD700] px-8 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#1E293B] transition-colors group"
          >
            Összes referencia megtekintése
            <LucideIcons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Lazy-loaded HTML5 Video — uses IntersectionObserver to defer video element creation until in viewport.
const LazyVideo = ({ src, title, contentType = "video/mp4", aspect = "9/16" }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "300px 0px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden rounded-2xl bg-black shadow-2xl"
      style={{ aspectRatio: aspect }}
      data-testid="lazy-video"
    >
      {inView ? (
        <video
          className="absolute inset-0 w-full h-full object-cover"
          controls
          playsInline
          preload="metadata"
          data-testid="video-player"
        >
          <source src={src} type={contentType} />
          A böngészője nem támogatja a videó lejátszást.
        </video>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0F172A] to-[#1E293B]">
          <div className="flex flex-col items-center text-white/50">
            <LucideIcons.Film className="w-14 h-14 mb-2" />
            <span className="text-xs uppercase tracking-wider">Videó betöltés...</span>
          </div>
        </div>
      )}
      {title && (
        <span className="absolute bottom-3 left-3 right-3 pointer-events-none text-white text-xs font-semibold uppercase tracking-wider drop-shadow-lg line-clamp-2">
          {title}
        </span>
      )}
    </div>
  );
};

// Gallery Section — full grid + lightbox (used on /referenciak page)
const Gallery = ({ embedded = false }) => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const content = useSiteContent();

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery?t=${Date.now()}`);
        setGalleryImages(response.data.images || []);
      } catch (err) {
        console.error("Error fetching gallery:", err);
        setGalleryImages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const closeLightbox = () => setSelectedImage(null);

  return (
    <section id="referenciak" className={`${embedded ? "py-16" : "py-24"} bg-white`} data-testid="gallery-full">
      <div className="max-w-7xl mx-auto px-6">
        {!embedded && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block bg-[#FFD700] text-[#0F172A] px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4"
            >
              {content?.gallery_badge || "Munkáink"}
            </motion.span>
            <motion.h2
              variants={fadeInUp}
              className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-bold text-[#0F172A]"
            >
              {content?.gallery_title || "REFERENCIÁK"}
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="mt-4 text-gray-600 max-w-2xl mx-auto"
            >
              {content?.gallery_description || "Tekintse meg korábbi projektjeinket."}
            </motion.p>
          </motion.div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-10 h-10 border-4 border-[#FFD700] border-t-transparent rounded-full" />
          </div>
        ) : galleryImages.length === 0 ? (
          <div className="text-center py-12">
            <LucideIcons.ImageOff className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Hamarosan feltöltjük a referencia képeinket.</p>
          </div>
        ) : (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-3 gap-4"
          >
            {galleryImages.map((image, index) => (
              <motion.div
                key={image.id || index}
                variants={fadeInUp}
                data-testid={`gallery-item-${index}`}
                className="gallery-item relative aspect-square cursor-pointer group"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={getImageUrl(image.thumbnail || image.src)}
                  alt={image.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#0F172A]/0 group-hover:bg-[#0F172A]/60 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity text-center px-4">
                    <LucideIcons.ZoomIn className="w-8 h-8 text-white mx-auto mb-2" />
                    <span className="text-white font-bold uppercase tracking-wider text-sm">
                      {image.alt}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Lightbox - uses full resolution image */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-[#FFD700] transition-colors"
              data-testid="lightbox-close"
            >
              <LucideIcons.X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={getImageUrl(selectedImage.src)}
              alt={selectedImage.alt}
              className="max-w-full max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-center font-medium">
              {selectedImage.alt}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const settings = useSiteSettings();
  const content = useSiteContent();
  const contactImage = getImageUrl(settings?.contact);

  const email = content?.contact_email || "paintroadkft@gmail.com";
  const phone = content?.contact_phone || "+36 30 383 3980";
  const phoneClean = phone.replace(/\s/g, "");

  return (
    <section id="kapcsolat" className="py-24 bg-[#F3F4F6]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-16 items-center"
        >
          <motion.div variants={fadeInUp}>
            <span className="inline-block bg-[#0F172A] text-[#FFD700] px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4">
              {content?.contact_badge || "Elérhetőségek"}
            </span>
            <h2 className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-bold text-[#0F172A] mb-4">
              {content?.contact_title || "KAPCSOLAT"}
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              {content?.contact_description || "Árajánlatért keressen minket bizalommal az alábbi elérhetőségek egyikén:"}
            </p>
            
            <div className="space-y-6">
              <a
                href={`mailto:${email}`}
                data-testid="contact-email"
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 bg-[#0F172A] flex items-center justify-center group-hover:bg-[#FFD700] transition-colors">
                  <LucideIcons.Mail className="w-6 h-6 text-[#FFD700] group-hover:text-[#0F172A] transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wider">E-mail</p>
                  <p className="text-lg font-semibold text-[#0F172A] group-hover:text-[#0F172A]/70 transition-colors">
                    {email}
                  </p>
                </div>
              </a>

              <a
                href={`tel:${phoneClean}`}
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 bg-[#0F172A] flex items-center justify-center group-hover:bg-[#FFD700] transition-colors">
                  <LucideIcons.Phone className="w-6 h-6 text-[#FFD700] group-hover:text-[#0F172A] transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wider">Telefon</p>
                  <p className="text-lg font-semibold text-[#0F172A] group-hover:text-[#0F172A]/70 transition-colors">
                    {phone}
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4" data-testid="contact-address">
                <div className="w-14 h-14 bg-[#0F172A] flex items-center justify-center">
                  <LucideIcons.MapPin className="w-6 h-6 text-[#FFD700]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wider">Cím</p>
                  <p className="text-lg font-semibold text-[#0F172A]">
                    {content?.contact_address || "1111 Budapest, Példa utca 1."}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={`mailto:${email}`}
                className="btn-primary inline-flex items-center gap-2"
              >
                <LucideIcons.Mail className="w-5 h-5" />
                {content?.contact_cta_email || "E-mail Küldése"}
              </a>
              <a
                href={`tel:${phoneClean}`}
                className="btn-secondary inline-flex items-center gap-2"
              >
                <LucideIcons.Phone className="w-5 h-5" />
                {content?.contact_cta_phone || "Hívjon Minket"}
              </a>
            </div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="relative"
          >
  
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Social Media icon components (defined outside SocialMedia to avoid re-creation on each render)
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1Z" />
  </svg>
);
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12Z" />
  </svg>
);
const InstagramIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2.16c3.2 0 3.58.02 4.85.07 1.17.06 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.05 1.27.07 1.65.07 4.85s-.02 3.58-.07 4.85c-.06 1.17-.25 1.8-.41 2.23a3.7 3.7 0 0 1-.9 1.38 3.7 3.7 0 0 1-1.38.9c-.42.16-1.06.36-2.23.41-1.27.05-1.65.07-4.85.07s-3.58-.02-4.85-.07c-1.17-.06-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.18 15.58 2.16 15.2 2.16 12s.02-3.58.07-4.85c.06-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.18 8.8 2.16 12 2.16ZM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.86 5.86 0 0 0-2.12 1.38A5.86 5.86 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91a5.86 5.86 0 0 0 1.38 2.12 5.86 5.86 0 0 0 2.12 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.86 5.86 0 0 0 2.12-1.38 5.86 5.86 0 0 0 1.38-2.12c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.86 5.86 0 0 0-1.38-2.12A5.86 5.86 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0Zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32Zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88Z" />
  </svg>
);

const SOCIAL_LINKS = [
  { name: "TikTok", handle: "@paintroadkft", url: "https://www.tiktok.com/@paintroadkft?_r=1&_t=ZN-95yLB3kRywo", Icon: TikTokIcon, accent: "from-[#FF0050] to-[#00F2EA]" },
  { name: "Facebook", handle: "PaintRoad Kft.", url: "https://www.facebook.com/share/1HBx9eM82R/?mibextid=wwXIfr", Icon: FacebookIcon, accent: "from-[#1877F2] to-[#0A59D1]" },
  { name: "Instagram", handle: "@paintroadkft", url: "https://www.instagram.com/paintroadkft?igsh=MWFra2F2c291N2o2YQ==", Icon: InstagramIcon, accent: "from-[#F58529] via-[#DD2A7B] to-[#8134AF]" }
];

// Social Media Section
const SocialMedia = () => {
  const socials = SOCIAL_LINKS;

  return (
    <section id="kozossegi" className="py-24 bg-[#0F172A] relative overflow-hidden" data-testid="social-section">
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, #FFD700 1px, transparent 0)",
        backgroundSize: "32px 32px"
      }} />

      <div className="max-w-7xl mx-auto px-6 relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-1 bg-[#FFD700]/10 text-[#FFD700] text-xs uppercase tracking-[0.2em] font-semibold rounded-full mb-4"
          >
            Kövess minket
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight"
          >
            Közösségi <span className="text-[#FFD700]">Felületek</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="mt-4 text-white/60 text-base max-w-2xl mx-auto"
          >
            Nézd meg legfrissebb munkáinkat és mindennapjainkat a közösségi oldalainkon.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {socials.map((s) => (
            <motion.a
              key={s.name}
              variants={fadeInUp}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-${s.name.toLowerCase()}`}
              className="group relative bg-white/[0.03] border border-white/10 rounded-2xl p-8 backdrop-blur-sm hover:border-[#FFD700]/50 transition-all duration-300 overflow-hidden"
            >
              <div className={`absolute -top-24 -right-24 w-48 h-48 rounded-full bg-gradient-to-br ${s.accent} opacity-20 group-hover:opacity-40 blur-2xl transition-opacity duration-500`} />

              <div className="relative flex flex-col items-start gap-6">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${s.accent} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <s.Icon className="w-7 h-7" />
                </div>

                <div>
                  <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase tracking-wider">
                    {s.name}
                  </h3>
                  <p className="text-white/50 text-sm mt-1">{s.handle}</p>
                </div>

                <div className="flex items-center gap-2 text-[#FFD700] text-sm font-semibold uppercase tracking-wider group-hover:gap-4 transition-all duration-300">
                  Követés
                  <LucideIcons.ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();
  const content = useSiteContent();
  const menuItems = useMenu();

  const copyrightText = (content?.footer_copyright || "© {year} PaintRoad Kft. Minden jog fenntartva.")
    .replace("{year}", currentYear);

  return (
    <footer className="bg-[#0F172A] py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="PaintRoad Logo" className="w-10 h-10 object-contain" />
            <span className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase tracking-wider">
              PaintRoad Kft.
            </span>
          </div>

          <div className="flex items-center gap-8">
            {menuItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                className="text-white/60 hover:text-[#FFD700] transition-colors text-sm uppercase tracking-wider"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-white/10 text-center">
          <p className="text-white/40 text-sm">
            {copyrightText}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Shared site data fetcher hook (used by HomePage and ReferencesPage)
const useSiteData = () => {
  const [siteSettings, setSiteSettings] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const [settingsRes, contentRes, menuRes] = await Promise.all([
          axios.get(`${API}/site-settings`),
          axios.get(`${API}/site-content`),
          axios.get(`${API}/menu`)
        ]);
        if (!mounted) return;
        setSiteSettings(settingsRes.data.images);
        setSiteContent(contentRes.data.content);
        setMenuItems(menuRes.data.items);
      } catch (err) {
        if (!mounted) return;
        console.error("Error fetching site data:", err);
        setSiteSettings({});
        setSiteContent({});
        setMenuItems([
          { id: "1", label: "Szolgáltatások", href: "#szolgaltatasok" },
          { id: "2", label: "Referenciák", href: "/referenciak" },
          { id: "3", label: "Kapcsolat", href: "#kapcsolat" },
          { id: "4", label: "Közösségi felületek", href: "#kozossegi" }
        ]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, []);

  return { siteSettings, siteContent, menuItems, loading };
};

const SiteDataProviders = ({ siteSettings, siteContent, menuItems, children }) => (
  <SiteSettingsContext.Provider value={siteSettings}>
    <SiteContentContext.Provider value={siteContent}>
      <MenuContext.Provider value={menuItems}>{children}</MenuContext.Provider>
    </SiteContentContext.Provider>
  </SiteSettingsContext.Provider>
);

const LoadingScreen = () => (
  <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
    <div className="animate-spin w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full" />
  </div>
);

// Home Page Component with Settings Provider
const HomePage = () => {
  const { siteSettings, siteContent, menuItems, loading } = useSiteData();
  useEffect(() => {
    // If there's a hash in the URL, scroll to it after content mounts
    if (!loading && window.location.hash) {
      const id = window.location.hash.slice(1);
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [loading]);

  if (loading) return <LoadingScreen />;

  return (
    <SiteDataProviders siteSettings={siteSettings} siteContent={siteContent} menuItems={menuItems}>
      <Navigation />
      <Hero />
      <Services />
      <GalleryPreview />
      <Contact />
      <SocialMedia />
      <Footer />
    </SiteDataProviders>
  );
};

// References Page (/referenciak) — full gallery + YouTube Shorts video
const ReferencesPage = () => {
  const { siteSettings, siteContent, menuItems, loading } = useSiteData();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <SiteDataProviders siteSettings={siteSettings} siteContent={siteContent} menuItems={menuItems}>
      <Navigation />

      {/* Page header */}
      <section
        className="relative pt-32 pb-20 bg-[#0F172A] overflow-hidden"
        data-testid="references-header"
      >
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(135deg, #FFD700 0 18px, transparent 18px 70px)"
          }}
        />
        <div
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(255,215,0,0.15) 0%, rgba(255,215,0,0) 65%)"
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#FFD700] transition-colors text-sm uppercase tracking-wider mb-8"
            data-testid="back-home-link"
          >
            <LucideIcons.ArrowLeft className="w-4 h-4" />
            Vissza a főoldalra
          </Link>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block bg-[#FFD700] text-[#0F172A] px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4"
            >
              Munkáink
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              className="font-['Barlow_Condensed'] text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white uppercase leading-none mb-4"
            >
              REFEREN<span className="text-[#FFD700]">CIÁK</span>
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-white/70 text-lg max-w-2xl"
            >
              Tekintse meg korábbi projektjeinket — útburkolati jelek, parkolóházak, sportpályák és ipari padlójelölések. Videó és fotók egy helyen.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Video section */}
      <VideosSection />

      {/* Full gallery */}
      <Gallery embedded={true} />

      <Footer />
    </SiteDataProviders>
  );
};

// Videos Section — fetches uploaded videos from MongoDB and renders LazyVideo for each.
const VideosSection = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/videos`)
      .then((res) => mounted && setVideos(res.data.videos || []))
      .catch(() => mounted && setVideos([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <section className="py-12 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center">
          <div className="animate-spin w-10 h-10 border-4 border-[#FFD700] border-t-transparent rounded-full" />
        </div>
      </section>
    );
  }

  if (videos.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-[#F3F4F6]" data-testid="video-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block bg-[#0F172A] text-[#FFD700] px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4"
          >
            Videó referencia
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#0F172A]"
          >
            MUNKA KÖZBEN
          </motion.h2>
        </motion.div>

        <div className={`grid gap-6 justify-items-center ${videos.length === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
          {videos.map((v) => (
            <div key={v.id} className="w-full max-w-sm">
              <LazyVideo
                src={`${BACKEND_URL}${v.src}`}
                title={v.alt}
                contentType={v.content_type || "video/mp4"}
                aspect="9/16"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/referenciak" element={<ReferencesPage />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
